import { IslandType } from '@rta/rta-blueprint-library';
import type { Island, Kitchen } from '@rta/rta-blueprint-library';

import { getKitchenLayoutLabel } from './get-kitchen-layout-label';

export type GetLayoutHeaderDescriptionParams = {
	kitchen: Kitchen;
};

/**
 * Builds the "Kitchen Layout | Featured Island" string for the page-header
 * top-right block — matching the historic PDF blueprints.
 *
 * Examples:
 *   "LINEAR W/ SMOKER | 10FT GRILL ISLAND"
 *   "L-SHAPE | 12FT GRILL ISLAND"
 *   "GALLEY | 9FT BAR ISLAND"            (no grill present)
 *   "CORNER STAND"                        (lone non-grill / non-bar island)
 *
 * Featured-island selection rules:
 *   1. Largest grill island (by `size.width`) if any grill islands exist.
 *   2. Otherwise largest bar island if any bar islands exist.
 *   3. Otherwise — if there is only one island and it's neither grill nor
 *      bar — use that island's own description name (e.g., "Corner Stand").
 *   4. Otherwise no featured island is appended.
 */
const getFirstSegment = (description: string | null | undefined): string => {
	if (!description) return '';
	return (description.split(' | ')[0] ?? '').trim();
};

const findLargestByType = (islands: Array<Island>, type: IslandType): Island | null => {
	const matching = islands.filter((island) => island.type === type);
	if (matching.length === 0) return null;
	return matching.reduce((largest, current) => {
		return current.size.width > largest.size.width ? current : largest;
	});
};

// MOKS is signalled by the kitchen's countertop material key OR surround key starting with 'mok'.
const isMoksKitchen = (kitchen: Kitchen): boolean => {
	const countertopKey = kitchen.material?.countertop?.key ?? '';
	const surroundKey = kitchen.material?.surround?.key ?? '';
	return countertopKey.toLowerCase().startsWith('mok') || surroundKey.toLowerCase().startsWith('mok');
};

export const getLayoutHeaderDescription = (params: GetLayoutHeaderDescriptionParams): string => {
	const { kitchen } = params;
	const baseLayout = getKitchenLayoutLabel({ kitchen, variant: 'header' });
	const layout = isMoksKitchen(kitchen) ? `MOKS | ${baseLayout}` : baseLayout;
	const islands = kitchen.design.islands.children;

	const featured = findLargestByType(islands, IslandType.Grill)
		?? findLargestByType(islands, IslandType.Bar)
		?? (islands.length === 1 ? islands[0] : null);

	if (!featured) return layout;

	const featuredName = getFirstSegment(featured.description);
	if (!featuredName) return layout;

	return `${layout} | ${featuredName}`;
};
